import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spin } from "antd";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import api from "services/api";
import "./FacilityCalendarPage.css";

const STATUS_COLORS = {
  reserved_unpaid: "#1890ff",  // blue
  reserved_paid: "#52c41a",    // green
  pending: "#fa8c16",          // orange
};

const FacilityCalendarPage = () => {
  const { registrationId } = useParams();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  const fetchAvailability = (startDate, endDate) => {
    const params = new URLSearchParams();
    params.append("registration_id", registrationId);
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);

    api.post("camp/get_facility_availability", params)
      .then((res) => {
        const data = res.data;
        if (data.result) {
          setEvents(data.data || []);
        } else {
          setError(data.msg || "Unable to load availability");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Unable to load facility availability.");
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!registrationId) {
      setError("Invalid calendar link");
      setLoading(false);
      return;
    }
    // Load initial 4-month range
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 3, 0);
    fetchAvailability(
      start.toISOString().split("T")[0],
      end.toISOString().split("T")[0]
    );
  }, [registrationId]);

  const handleDatesSet = (dateInfo) => {
    const start = dateInfo.startStr.split("T")[0];
    const end = dateInfo.endStr.split("T")[0];
    setLoading(true);
    fetchAvailability(start, end);
  };

  const getEventColor = (evt) => {
    const status = (evt.status || "").toLowerCase();
    if (status === "pending") return STATUS_COLORS.pending;
    if (status === "approved") {
      return evt.paid === 1 || evt.paid === "1"
        ? STATUS_COLORS.reserved_paid
        : STATUS_COLORS.reserved_unpaid;
    }
    return STATUS_COLORS.pending;
  };

  const calendarEvents = events.map((evt) => {
    const startTime = evt.event_start_time || "00:00:00";
    const endTime = evt.event_end_time || "23:59:59";
    const isPaid = evt.paid === 1 || evt.paid === "1";
    const status = (evt.status || "").toLowerCase();
    let title = evt.facility || "Reserved";
    if (status === "pending") title += " (Pending)";
    else if (status === "approved") title += isPaid ? " (Reserved)" : " (Reserved - Pending Payment)";

    return {
      id: evt.id,
      title,
      start: `${evt.event_date}T${startTime}`,
      end: `${evt.event_date}T${endTime}`,
      backgroundColor: getEventColor(evt),
      borderColor: getEventColor(evt),
    };
  });

  if (error) {
    return (
      <div className="facility-calendar-error">
        <h2>Calendar Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="facility-calendar-page">
      <div className="calendar-header">
        <h1>Facility Availability</h1>
        <p>View current reservations and availability</p>
      </div>

      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: STATUS_COLORS.pending }} />
          <span>Pending Approval</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: STATUS_COLORS.reserved_unpaid }} />
          <span>Reserved - Pending Payment</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: STATUS_COLORS.reserved_paid }} />
          <span>Reserved</span>
        </div>
      </div>

      <Spin spinning={loading}>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek",
          }}
          events={calendarEvents}
          datesSet={handleDatesSet}
          height="auto"
          eventTimeFormat={{
            hour: "numeric",
            minute: "2-digit",
            meridiem: "short",
          }}
          selectable={false}
          editable={false}
        />
      </Spin>
    </div>
  );
};

export default FacilityCalendarPage;
